import * as React from 'react'
import { useCallback } from 'react'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'

import { ScheduleMenuItem } from './ScheduleMenuItem'
import { useSchedulingWizard } from '../wizardContext'
import type { IScheduleSettingsDetail } from '@user/store/closingRequirements'
import { ENTITY_TYPES } from '@sales-center/common'

type ScheduleButtonProps = {
  disabled?: boolean
  disableElevation?: boolean
  scheduleSettings: IScheduleSettingsDetail
}

export function ScheduleButton(props: ScheduleButtonProps) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])
  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const {
    disabled,
    disableElevation,
    scheduleSettings: {
      scheduleVisibilitySettings: {
        serviceScheduleEnabled,
        paidEstimateScheduleEnabled,
        freeEstimateScheduleEnabled,
      },
      scheduleClosingRequirements: {
        requireServiceSchedule,
        requireFreeEstimateSchedule,
        requirePaidEstimateSchedule,
        requireACWOppsSchedulingService,
      },
    },
  } = props

  const [{ entityId, entityType }] = useSchedulingWizard()

  return (
    <div>
      <Button
        id="scheduled-button"
        disabled={disabled}
        data-test-id={`scheduled-${entityId}`}
        aria-controls={open ? 'scheduled-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        size="small"
        variant="contained"
        fullWidth
        disableElevation={disableElevation}
      >
        Schedule
      </Button>
      <Menu
        id="scheduled-menu"
        anchorEl={anchorEl}
        open={open}
        elevation={8}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'scheduled-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{ mt: 0.8 }}
        slotProps={{
          paper: {
            sx: {
              minWidth:
                document?.getElementById('scheduled-button')?.offsetWidth ||
                'auto',
            },
          },
        }}
      >
        {freeEstimateScheduleEnabled && (
          <ScheduleMenuItem
            mode="free"
            onClose={handleClose}
            entityId={entityId}
            required={requireFreeEstimateSchedule}
            disabled={!freeEstimateScheduleEnabled}
          />
        )}
        {paidEstimateScheduleEnabled && (
          <ScheduleMenuItem
            mode="paid"
            onClose={handleClose}
            entityId={entityId}
            required={requirePaidEstimateSchedule}
            disabled={!paidEstimateScheduleEnabled}
          />
        )}
        {serviceScheduleEnabled && (
          <ScheduleMenuItem
            mode="service"
            onClose={handleClose}
            entityId={entityId}
            required={requireServiceSchedule}
            disabled={!serviceScheduleEnabled}
            requireACWOppScheduling={
              serviceScheduleEnabled &&
              requireServiceSchedule &&
              requireACWOppsSchedulingService &&
              entityType == ENTITY_TYPES.Opportunity
            }
          />
        )}
      </Menu>
    </div>
  )
}
